import {Button, DatePicker, Input} from 'antd';
import {useCallback, useEffect, useState} from 'react';

import {Locale} from '../../../provider/locale/locale';
import {useLocale} from '../../../provider/locale/locale-hook';
import {useFormRules} from '../../../service/form/form-rules-hook';
import {
    CommonPromotionStatusEnum,
    PromotionFormFieldsEnum,
    PromotionFormType,
} from '../../../service/promotions/promotions-type';
import {Form, FormInstance} from '../../../typings/antd';
import {PublicationWizardStep} from '../../publication-wizard/publication-wizard-step';

// Yandex help limits for promotions.
const PROMOTION_TITLE_MAX_LENGTH = 70;
const PROMOTION_DESCRIPTION_MAX_LENGTH = 200;

type SubmittingActionType = 'draft' | 'publish' | null;

type PropsType = {
    formInstance: FormInstance<PromotionFormType>;
    formMode: 'create' | 'edit';
    onPreviousStep: VoidFunction;
    onSubmit: (values: PromotionFormType, status: CommonPromotionStatusEnum) => Promise<void>;
    onStepActivated: (width: number) => void;
    width: number;
};

const dateRowStyle = {display: 'flex', gap: 16};

// Final wizard step: the remaining fields + submit.
export function PromotionDetailsStep(props: PropsType): JSX.Element {
    const {formInstance, formMode, onPreviousStep, onSubmit, onStepActivated, width} = props;

    const {requiredFieldRule, validUrlRule, maxLengthFieldRule} = useFormRules();
    const {getLocalizedString} = useLocale();

    const [submittingAction, setSubmittingAction] = useState<SubmittingActionType>(null);

    const publishAt = Form.useWatch(PromotionFormFieldsEnum.PublishAt, {form: formInstance, preserve: true});

    useEffect(() => {
        onStepActivated(width);
    }, [onStepActivated, width]);

    const handleSubmit = useCallback(
        async (status: CommonPromotionStatusEnum) => {
            setSubmittingAction(status === CommonPromotionStatusEnum.Draft ? 'draft' : 'publish');

            try {
                await formInstance.validateFields();
                await onSubmit(formInstance.getFieldsValue(true), status);
            } finally {
                setSubmittingAction(null);
            }
        },
        [formInstance, onSubmit]
    );

    const isBusy = submittingAction !== null;
    const activeStatus = publishAt ? CommonPromotionStatusEnum.Planned : CommonPromotionStatusEnum.Sent;
    const publishLangKey = publishAt ? 'BUTTON__SCHEDULE' : 'BUTTON__PUBLISH';

    return (
        <PublicationWizardStep
            footer={
                formMode === 'create' ? (
                    <Button
                        disabled={isBusy}
                        loading={submittingAction === 'draft'}
                        onClick={() => handleSubmit(CommonPromotionStatusEnum.Draft)}
                    >
                        <Locale stringKey="POSTS_FORM__BUTTON__SAVE_DRAFT" />
                    </Button>
                ) : null
            }
            leftFooterButtonConfig={{langKeyType: 'BUTTON__BACK', onClick: onPreviousStep}}
            rightFooterButtonConfig={{
                disabled: isBusy,
                langKeyType: formMode === 'create' ? publishLangKey : 'BUTTON__UPDATE',
                loading: submittingAction === 'publish',
                onClick: () => handleSubmit(activeStatus),
            }}
        >
            <Form.Item<PromotionFormType>
                label={<Locale stringKey="PROMOTIONS__FORM__TITLE" />}
                name={PromotionFormFieldsEnum.Title}
                rules={[requiredFieldRule, maxLengthFieldRule(PROMOTION_TITLE_MAX_LENGTH)]}
                tooltip={getLocalizedString('PROMOTIONS__FORM__TITLE__TOOLTIP')}
            >
                <Input maxLength={PROMOTION_TITLE_MAX_LENGTH} showCount />
            </Form.Item>

            <Form.Item<PromotionFormType>
                label={<Locale stringKey="PROMOTIONS__FORM__DESCRIPTION" />}
                name={PromotionFormFieldsEnum.Description}
                rules={[requiredFieldRule, maxLengthFieldRule(PROMOTION_DESCRIPTION_MAX_LENGTH)]}
                tooltip={getLocalizedString('PROMOTIONS__FORM__DESCRIPTION__TOOLTIP')}
            >
                <Input.TextArea
                    autoSize={{minRows: 3, maxRows: 8}}
                    maxLength={PROMOTION_DESCRIPTION_MAX_LENGTH}
                    showCount
                />
            </Form.Item>

            <Form.Item<PromotionFormType>
                label={<Locale stringKey="PROMOTIONS__FORM__URL" />}
                name={PromotionFormFieldsEnum.Url}
                rules={[validUrlRule()]}
                tooltip={getLocalizedString('PROMOTIONS__FORM__URL__TOOLTIP')}
            >
                <Input />
            </Form.Item>

            <div style={dateRowStyle}>
                <Form.Item<PromotionFormType>
                    label={<Locale stringKey="PROMOTIONS__FORM__START_AT" />}
                    name={PromotionFormFieldsEnum.StartAt}
                    rules={[requiredFieldRule]}
                    style={{flex: 1}}
                    tooltip={getLocalizedString('PROMOTIONS__FORM__ACTIVE_PERIOD__TOOLTIP')}
                >
                    <DatePicker showTime style={{width: '100%'}} />
                </Form.Item>

                <Form.Item<PromotionFormType>
                    label={<Locale stringKey="PROMOTIONS__FORM__FINISH_AT" />}
                    name={PromotionFormFieldsEnum.FinishAt}
                    rules={[requiredFieldRule]}
                    style={{flex: 1}}
                    tooltip={getLocalizedString('PROMOTIONS__FORM__ACTIVE_PERIOD__TOOLTIP')}
                >
                    <DatePicker showTime style={{width: '100%'}} />
                </Form.Item>
            </div>

            <div style={dateRowStyle}>
                <Form.Item<PromotionFormType>
                    label={<Locale stringKey="PROMOTIONS__FORM__PUBLISH_AT" />}
                    name={PromotionFormFieldsEnum.PublishAt}
                    style={{flex: 1}}
                    tooltip={getLocalizedString('PROMOTIONS__FORM__DISPLAY_PERIOD__TOOLTIP')}
                >
                    <DatePicker showTime style={{width: '100%'}} />
                </Form.Item>

                <Form.Item<PromotionFormType>
                    label={<Locale stringKey="PROMOTIONS__FORM__DELETE_AT" />}
                    name={PromotionFormFieldsEnum.DeletedAt}
                    style={{flex: 1}}
                    tooltip={getLocalizedString('PROMOTIONS__FORM__DISPLAY_PERIOD__TOOLTIP')}
                >
                    <DatePicker showTime style={{width: '100%'}} />
                </Form.Item>
            </div>
        </PublicationWizardStep>
    );
}
